import type { LanguageEnum } from 'js/src/client';
import type {
    SerializedTranscript,
    TranscriptTranslations,
} from 'js/src/data/transcript/transcript-types';
import {
    createLexicalEditor,
    getNextTranslationEditorName,
    TRANSCRIPT_EDITOR_CONFIG,
} from 'js/src/data/transcript/transcript-utils';
import type { TranscriptTranslationsType } from 'js/src/data/translated-editors/translated-editors-types';
import { ImmutableFromJS, type ImmutableMap } from 'js/src/libs/consts';

type TranslationsEditorReducerType = {
    sceneGroupId: string;
    translations: TranscriptTranslationsType;
}[];

export const reduceTranscriptsToTranslationEditorsMap = (
    transcripts: Record<string, SerializedTranscript>,
) => {
    const sceneGroupIdsWithTranslations = Object.keys(transcripts).filter((sceneGroupId) => {
        const hasEditorState = !!transcripts[sceneGroupId].editorState;
        const hasValidTranslations =
            transcripts[sceneGroupId]?.translations &&
            Object.keys(transcripts[sceneGroupId].translations).length > 0;

        return hasEditorState && hasValidTranslations;
    });

    const sceneGroupToTranslationEditorsMaps = sceneGroupIdsWithTranslations.reduce(
        (allTranslations: TranslationsEditorReducerType, sceneGroupId: string) => {
            const transcript = transcripts[sceneGroupId];
            if (!transcript.translations) {
                return allTranslations;
            }
            const translations = ImmutableFromJS<TranscriptTranslations>(transcript.translations);
            const translationsInSceneGroup = reduceTranslationsToEditorsMap(
                sceneGroupId,
                translations,
            );
            if (translationsInSceneGroup) {
                allTranslations.push(translationsInSceneGroup);
            }
            return allTranslations;
        },
        [],
    );

    return sceneGroupToTranslationEditorsMaps;
};

export const reduceTranslationsToEditorsMap = (
    sceneGroupId: string,
    translations: ImmutableMap<TranscriptTranslations>,
) => {
    const languageKeys = translations.keySeq();
    const hasTranslations = languageKeys.size !== undefined && languageKeys.size > 0;
    if (!hasTranslations) {
        return;
    }

    const translationEditorMap = languageKeys.reduce<TranscriptTranslationsType>(
        (currMap: TranscriptTranslationsType, langCode: LanguageEnum) => {
            const editor = createLexicalEditor(
                TRANSCRIPT_EDITOR_CONFIG,
                getNextTranslationEditorName(),
            );
            const parsedEditorState = editor.parseEditorState(
                JSON.stringify(translations.get(langCode).get('editorState')),
            );
            editor.setEditorState(parsedEditorState);

            currMap[langCode] = {
                editor,
                lexicalControlsItsOwnState: false,
            };
            return currMap;
        },
        {} as TranscriptTranslationsType,
    );

    return {
        sceneGroupId: sceneGroupId,
        translations: translationEditorMap,
    };
};
